import { Route, Switch, Redirect } from 'wouter';
import { SignedIn, SignedOut, RedirectToSignIn } from '@clerk/clerk-react';
import { Suspense, lazy, type ReactNode } from 'react';
import { LEGAL_DOCS } from '@shared/legal/index.ts';

/**
 * Cycle 24 (2026-08-19) — route-level code splitting.
 *
 * Every page used to be a static import, so Vite emitted ONE 2.1 MB chunk
 * (495 kB gzip) that a first-time landing visitor had to download, parse and
 * execute before a single pixel rendered — the node editor, the whole admin
 * console, Stripe billing and the blog included. That is the "site çok yavaş"
 * report from 2026-08-19.
 *
 * Each page is now its own `lazy()` chunk, so a visitor pays for the route
 * they actually opened plus the shared vendor chunks (see `manualChunks` in
 * `client/vite.config.ts`). Wouter renders exactly one branch of the Switch,
 * so at most one lazy chunk is ever in flight.
 *
 * `LEGAL_DOCS` stays a static import — it is a tiny slug registry that the
 * route table itself iterates to build its <Route> list.
 */

const LoginPage = lazy(() =>
  import('@/features/auth/pages/LoginPage.tsx').then((m) => ({ default: m.LoginPage })),
);
const SsoCallbackPage = lazy(() =>
  import('@/features/auth/pages/SsoCallbackPage.tsx').then((m) => ({ default: m.SsoCallbackPage })),
);
const MyWorkflowsPage = lazy(() =>
  import('@/features/workflows/pages/MyWorkflowsPage.tsx').then((m) => ({ default: m.MyWorkflowsPage })),
);
const WorkflowEditorPage = lazy(() =>
  import('@/features/workflows/pages/WorkflowEditorPage.tsx').then((m) => ({ default: m.WorkflowEditorPage })),
);
const ProductsPage = lazy(() =>
  import('@/features/products/pages/ProductsPage.tsx').then((m) => ({ default: m.ProductsPage })),
);
const RunsPage = lazy(() =>
  import('@/features/runs/pages/RunsPage.tsx').then((m) => ({ default: m.RunsPage })),
);
const StoresPage = lazy(() =>
  import('@/features/stores/pages/StoresPage.tsx').then((m) => ({ default: m.StoresPage })),
);
const AccountPage = lazy(() =>
  import('@/features/account/pages/AccountPage.tsx').then((m) => ({ default: m.AccountPage })),
);
const EmailPreferencesPage = lazy(() =>
  import('@/features/account/pages/EmailPreferencesPage.tsx').then((m) => ({ default: m.EmailPreferencesPage })),
);
const UnsubscribePage = lazy(() =>
  import('@/features/account/pages/UnsubscribePage.tsx').then((m) => ({ default: m.UnsubscribePage })),
);
const HelpPage = lazy(() =>
  import('@/features/support/pages/HelpPage.tsx').then((m) => ({ default: m.HelpPage })),
);
const AdminDashboardPage = lazy(() =>
  import('@/features/admin/pages/AdminDashboardPage.tsx').then((m) => ({ default: m.AdminDashboardPage })),
);
const UsersAdminPage = lazy(() =>
  import('@/features/admin/pages/UsersAdminPage.tsx').then((m) => ({ default: m.UsersAdminPage })),
);
const RunsAdminPage = lazy(() =>
  import('@/features/admin/pages/RunsAdminPage.tsx').then((m) => ({ default: m.RunsAdminPage })),
);
const SupportAdminPage = lazy(() =>
  import('@/features/admin/pages/SupportAdminPage.tsx').then((m) => ({ default: m.SupportAdminPage })),
);
const ContactMessagesAdminPage = lazy(() =>
  import('@/features/admin/pages/ContactMessagesAdminPage.tsx').then((m) => ({ default: m.ContactMessagesAdminPage })),
);
const FinanceAdminPage = lazy(() =>
  import('@/features/admin/pages/FinanceAdminPage.tsx').then((m) => ({ default: m.FinanceAdminPage })),
);
const CostMonitoringPage = lazy(() =>
  import('@/features/admin/pages/CostMonitoringPage.tsx').then((m) => ({ default: m.CostMonitoringPage })),
);
const PromptsAdminPage = lazy(() =>
  import('@/features/admin/pages/PromptsAdminPage.tsx').then((m) => ({ default: m.PromptsAdminPage })),
);
const LeadsAdminPage = lazy(() =>
  import('@/features/admin/pages/LeadsAdminPage.tsx').then((m) => ({ default: m.LeadsAdminPage })),
);
const AuditLogAdminPage = lazy(() =>
  import('@/features/admin/pages/AuditLogAdminPage.tsx').then((m) => ({ default: m.AuditLogAdminPage })),
);
const AnnouncementsAdminPage = lazy(() =>
  import('@/features/admin/pages/AnnouncementsAdminPage.tsx').then((m) => ({ default: m.AnnouncementsAdminPage })),
);
const WorkflowTemplatesAdminPage = lazy(() =>
  import('@/features/admin/pages/WorkflowTemplatesAdminPage.tsx').then((m) => ({ default: m.WorkflowTemplatesAdminPage })),
);
const WorkflowTemplateEditorPage = lazy(() =>
  import('@/features/admin/pages/WorkflowTemplateEditorPage.tsx').then((m) => ({ default: m.WorkflowTemplateEditorPage })),
);
const RunTicketsAdminPage = lazy(() =>
  import('@/features/admin/pages/RunTicketsAdminPage.tsx').then((m) => ({ default: m.RunTicketsAdminPage })),
);
const AboutPage = lazy(() =>
  import('@/features/company/pages/AboutPage.tsx').then((m) => ({ default: m.AboutPage })),
);
const ContactPage = lazy(() =>
  import('@/features/contact/pages/ContactPage.tsx').then((m) => ({ default: m.ContactPage })),
);
const PricingPage = lazy(() =>
  import('@/features/billing/pages/PricingPage.tsx').then((m) => ({ default: m.PricingPage })),
);
const BillingPage = lazy(() =>
  import('@/features/billing/pages/BillingPage.tsx').then((m) => ({ default: m.BillingPage })),
);
const LandingPage = lazy(() => import('@/features/marketing/pages/LandingPage.tsx'));
const LegalPage = lazy(() =>
  import('@/features/legal/pages/LegalPage.tsx').then((m) => ({ default: m.LegalPage })),
);
const BlogIndexPage = lazy(() =>
  import('@/features/blog/pages/BlogIndexPage.tsx').then((m) => ({ default: m.BlogIndexPage })),
);
const BlogPostPage = lazy(() =>
  import('@/features/blog/pages/BlogPostPage.tsx').then((m) => ({ default: m.BlogPostPage })),
);

function AuthGuard({ children }: { children: ReactNode }) {
  return (
    <>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
      <SignedIn>{children}</SignedIn>
    </>
  );
}

/**
 * Route-transition fallback. Deliberately empty: chunks are small and served
 * from the CDN, so a spinner would flash for ~50 ms and read as jank. The
 * previous behaviour (blank screen while 2.1 MB parsed) was strictly worse.
 */
function RouteFallback() {
  return <div style={{ minHeight: '100vh' }} />;
}

export function Routes() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Switch>
      <Route path="/login">
        <SignedOut>
          <LoginPage />
        </SignedOut>
        <SignedIn>
          <Redirect to="/akislar" />
        </SignedIn>
      </Route>

      <Route path="/sso-callback">
        <SsoCallbackPage />
      </Route>

      <Route path="/hakkimizda">
        <AboutPage />
      </Route>

      <Route path="/iletisim">
        <ContactPage />
      </Route>

      <Route path="/abonelik-tercihleri">
        <EmailPreferencesPage />
      </Route>

      <Route path="/abonelik-cikis">
        <UnsubscribePage />
      </Route>

      <Route path="/fiyatlandirma">
        <PricingPage />
      </Route>

      {/* Public SaaS legal pages — driven from the @shared/legal registry so
          adding a doc later is one entry. No AuthGuard (ad/payment reviewers
          must read them without an account). */}
      {LEGAL_DOCS.map((doc) => (
        <Route key={doc.slug} path={`/${doc.slug}`}>
          <LegalPage slug={doc.slug} />
        </Route>
      ))}

      {/* Public SEO/GEO blog — statically prerendered at build time
          (scripts/prerender-blog.ts). No AuthGuard. */}
      <Route path="/blog">
        <BlogIndexPage />
      </Route>
      <Route path="/blog/:slug">
        <BlogPostPage />
      </Route>

      <Route path="/akislar">
        <AuthGuard>
          <MyWorkflowsPage />
        </AuthGuard>
      </Route>

      <Route path="/akis/:id">
        <AuthGuard>
          <WorkflowEditorPage />
        </AuthGuard>
      </Route>

      <Route path="/urunler">
        <AuthGuard>
          <ProductsPage />
        </AuthGuard>
      </Route>

      <Route path="/calismalar">
        <AuthGuard>
          <RunsPage />
        </AuthGuard>
      </Route>

      <Route path="/magazalar">
        <AuthGuard>
          <StoresPage />
        </AuthGuard>
      </Route>

      <Route path="/hesap">
        <AuthGuard>
          <AccountPage />
        </AuthGuard>
      </Route>

      <Route path="/abonelik-fatura">
        <AuthGuard>
          <BillingPage />
        </AuthGuard>
      </Route>

      <Route path="/yardim">
        <AuthGuard>
          <HelpPage />
        </AuthGuard>
      </Route>

      <Route path="/admin">
        <AuthGuard>
          <AdminDashboardPage />
        </AuthGuard>
      </Route>
      <Route path="/admin/kullanicilar">
        <AuthGuard>
          <UsersAdminPage />
        </AuthGuard>
      </Route>
      <Route path="/admin/calismalar">
        <AuthGuard>
          <RunsAdminPage />
        </AuthGuard>
      </Route>
      <Route path="/admin/destek">
        <AuthGuard>
          <SupportAdminPage />
        </AuthGuard>
      </Route>
      <Route path="/admin/iletisim">
        <AuthGuard>
          <ContactMessagesAdminPage />
        </AuthGuard>
      </Route>
      <Route path="/admin/finans">
        <AuthGuard>
          <FinanceAdminPage />
        </AuthGuard>
      </Route>
      <Route path="/admin/maliyet">
        <AuthGuard>
          <CostMonitoringPage />
        </AuthGuard>
      </Route>
      <Route path="/admin/promptlar">
        <AuthGuard>
          <PromptsAdminPage />
        </AuthGuard>
      </Route>
      <Route path="/admin/leads">
        <AuthGuard>
          <LeadsAdminPage />
        </AuthGuard>
      </Route>
      <Route path="/admin/denetim">
        <AuthGuard>
          <AuditLogAdminPage />
        </AuthGuard>
      </Route>
      <Route path="/admin/duyurular">
        <AuthGuard>
          <AnnouncementsAdminPage />
        </AuthGuard>
      </Route>
      <Route path="/admin/sablonlar">
        <AuthGuard>
          <WorkflowTemplatesAdminPage />
        </AuthGuard>
      </Route>
      <Route path="/admin/sablonlar/:id">
        <AuthGuard>
          <WorkflowTemplateEditorPage />
        </AuthGuard>
      </Route>
      <Route path="/admin/calisma-talepleri">
        <AuthGuard>
          <RunTicketsAdminPage />
        </AuthGuard>
      </Route>

      <Route path="/">
        <SignedOut>
          <LandingPage />
        </SignedOut>
        <SignedIn>
          <Redirect to="/akislar" />
        </SignedIn>
      </Route>

      <Route>
        <NotFound />
      </Route>
      </Switch>
    </Suspense>
  );
}

function NotFound() {
  return (
    <div style={{ display: 'grid', placeItems: 'center', height: '100vh' }}>
      <div style={{ textAlign: 'center' }}>
        <h1 style={{ fontSize: 48, fontWeight: 600, margin: 0 }}>404</h1>
        <p style={{ color: 'var(--text-2)' }}>Bu sayfa yok.</p>
        <a href="/akislar" style={{ color: 'var(--text)' }}>← Akışlarıma dön</a>
      </div>
    </div>
  );
}
